import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  Space,
  Alert,
  Spin,
  message,
} from 'antd';
import {
  RobotOutlined,
  UserOutlined,
  SendOutlined,
  SaveOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotAgent } from '../../../types/krybot';
import styles from './AgentManagement.module.css';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AgentPromptBuilderTabProps {
  agent: KrybotAgent | null;
  agentData: Partial<KrybotAgent>;
  isCreating: boolean;
  onDataChange: (updates: Partial<KrybotAgent>) => void;
  onSave: () => void;
  loading: boolean;
}

export const AgentPromptBuilderTab: React.FC<AgentPromptBuilderTabProps> = ({
  agent,
  agentData,
  isCreating,
  onDataChange,
  onSave,
  loading,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with agent context if available
    if (agent && messages.length === 0) {
      const contextMessage: Message = {
        id: 'context',
        role: 'assistant',
        content: `I'm here to help you build and refine your agent "${agent.display_name || agent.name}". 

I have access to all 5 key fields that define your agent:
1. **Persona Prompt** - How the agent behaves
2. **Capabilities** - What actions the agent can perform
3. **Context** - Default context information
4. **Input Schema** - Structure of input data
5. **Output Schema** - Structure of output data

You can ask me to:
- Analyze and improve any of these fields
- Suggest new capabilities or context
- Help design input/output schemas
- Review the overall agent configuration

What would you like to work on?`,
        timestamp: new Date(),
      };
      setMessages([contextMessage]);
    } else if (isCreating && messages.length === 0) {
      const contextMessage: Message = {
        id: 'context',
        role: 'assistant',
        content: `I'm here to help you build a new agent from scratch.

I'll help you define all 5 key fields:
1. **Persona Prompt** - How the agent should behave
2. **Capabilities** - What actions the agent can perform
3. **Context** - Default context information
4. **Input Schema** - Structure of input data
5. **Output Schema** - Structure of output data

Let's start by understanding what you want this agent to do. What is the agent's primary purpose?`,
        timestamp: new Date(),
      };
      setMessages([contextMessage]);
    }
  }, [agent, isCreating]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const buildAgentContext = () => {
    return {
      name: agentData.name || agent?.name || 'New Agent',
      display_name: agentData.display_name || agent?.display_name,
      description: agentData.description || agent?.description,
      agent_type: agentData.agent_type || agent?.agent_type,
      persona_prompt: agentData.persona_prompt || agent?.persona_prompt || '',
      capabilities: agentData.capabilities || agent?.capabilities || {},
      default_context: agentData.default_context || agent?.default_context || {},
      input_schema: agentData.input_schema || agent?.input_schema || {},
      output_schema: agentData.output_schema || agent?.output_schema || {},
    };
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setChatLoading(true);

    try {
      const agentContext = buildAgentContext();
      
      // Build messages array for Krybot API
      const apiMessages = messages
        .filter(msg => msg.id !== 'context') // Exclude context message
        .map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));
      
      // Add the current user message
      apiMessages.push({
        role: 'user',
        content: userInput,
      });

      // Add system context about the agent
      const systemContext = `You are helping to build an AI agent. Here is the current agent configuration:

Agent Name: ${agentContext.name}
Agent Type: ${agentContext.agent_type || 'Not set'}
Description: ${agentContext.description || 'No description'}

Current Persona Prompt:
${agentContext.persona_prompt || 'Not set'}

Current Capabilities:
${JSON.stringify(agentContext.capabilities, null, 2)}

Current Default Context:
${JSON.stringify(agentContext.default_context, null, 2)}

Current Input Schema:
${JSON.stringify(agentContext.input_schema, null, 2)}

Current Output Schema:
${JSON.stringify(agentContext.output_schema, null, 2)}

You can suggest improvements to any of these fields. When suggesting changes, be specific about which field you're updating and provide the complete updated value.`;

      // Call Krybot API via prompt builder endpoint
      const response = await KrybotService.promptBuilderChat({
        messages: [
          { role: 'system', content: systemContext },
          ...apiMessages,
        ],
        agent_context: {
          name: agentContext.name,
          persona_prompt: agentContext.persona_prompt || '',
        },
      });

      const messageContent = response.message || 'I apologize, but I could not generate a response.';
      const rawContent = response.mapping?.raw || messageContent;
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: rawContent,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to process message';
      message.error(`Failed to process message: ${errorMessage}`);
      
      const errorChatMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I encountered an error: ${errorMessage}\n\nPlease check your Krybot API configuration or try again.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorChatMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleApplyToField = (field: 'persona_prompt' | 'capabilities' | 'default_context' | 'input_schema' | 'output_schema', value: string) => {
    try {
      let parsedValue: Record<string, unknown> | string = value;
      
      // For JSON fields, try to parse
      if (field !== 'persona_prompt') {
        try {
          parsedValue = JSON.parse(value);
        } catch {
          // If parsing fails, keep as string
        }
      }
      
      onDataChange({ [field]: parsedValue });
      message.success(`Applied to ${field.replace('_', ' ')}`);
    } catch (err) {
      message.error('Failed to apply value');
    }
  };

  return (
    <div className="agent-prompt-builder-tab">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Prompt Builder</h3>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={onSave}
          loading={loading}
        >
          Save Agent Changes
        </Button>
      </div>
      <Alert
        message="AI-Powered Prompt Builder"
        description="Chat with AI to build and refine your agent's 5 key fields: Persona, Capabilities, Context, Input Schema, and Output Schema. The AI has access to your current agent configuration and can suggest improvements."
        type="info"
        style={{ marginBottom: 16 }}
      />

      <Card className="prompt-builder-chat-card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 400px)', minHeight: '500px' }}>
        <div className="prompt-builder-messages" style={{ flex: 1, overflowY: 'auto', padding: '16px', marginBottom: '16px' }}>
          <List
            dataSource={messages}
            renderItem={(msg) => (
              <List.Item
                style={{
                  border: 'none',
                  padding: '12px 0',
                  backgroundColor: msg.role === 'user' ? 'transparent' : '#fafafa',
                }}
              >
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar
                    icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    style={{
                      backgroundColor: msg.role === 'user' ? '#1890ff' : '#52c41a',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong>{msg.role === 'user' ? 'You' : 'AI Assistant'}</Text>
                    <Paragraph style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Paragraph>
                    {msg.role === 'assistant' && (
                      <Space style={{ marginTop: 8 }}>
                        <Button
                          size="small"
                          icon={<CheckOutlined />}
                          onClick={() => {
                            // Try to extract persona prompt from response
                            const lines = msg.content.split('\n');
                            const promptStart = lines.findIndex(l => 
                              l.toLowerCase().includes('persona') || 
                              l.toLowerCase().includes('prompt')
                            );
                            if (promptStart >= 0) {
                              const promptText = lines.slice(promptStart + 1).join('\n').trim();
                              if (promptText.length > 50) {
                                handleApplyToField('persona_prompt', promptText);
                              }
                            }
                          }}
                        >
                          Apply to Persona
                        </Button>
                      </Space>
                    )}
                  </div>
                </Space>
              </List.Item>
            )}
          />
          <div ref={messagesEndRef} />
        </div>

        <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: '16px', flexShrink: 0 }}>
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask AI to help build or improve your agent..."
              rows={3}
              disabled={chatLoading || loading}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={chatLoading}
              disabled={loading}
              style={{ height: 'auto' }}
            >
              Send
            </Button>
          </Space.Compact>

          {chatLoading && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Spin />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

